import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Chip, Grid, Stack, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { ChartCard, Section, StatTile } from '../analytics/owner/parts';
import { AXIS, BASELINE, CURSOR_FILL, GAP, GRID, LEGEND, NEUTRAL, SERIES, TOOLTIP } from '../analytics/chartTheme';
import { Panel, TileGrid } from '../analytics/DashboardKit';
import { formatClock, formatPct, type DrillSpec } from '../analytics/owner/ownerData';
import { FAILURE_LABELS, rfqListTab, whenSgt, type OpsHealth, opsTileDrills, failureSummary, hourlyToday } from './opsHealth';
import { JumpTile } from './trackingParts';
import { jumpTo } from './useRpc';

// The Live Ops tab's headline tiles, today's charts and the failure list.

const nf = (n: number | null | undefined) => (n == null ? '-' : n.toLocaleString('en-SG'));

interface LiveOpsTilesProps {
  data: OpsHealth;
  drills: ReturnType<typeof opsTileDrills>;
  emailCount: number;
  portalCount: number;
  overdue: number;
  backlogCount: number;
  failureTotal: number;
  onDrill: (spec: DrillSpec) => void;
}

/** Open RFQs, today's intake and sends, 4-hour speed, and what needs a look. */
export function LiveOpsTiles({ data, drills, emailCount, portalCount, overdue, backlogCount, failureTotal, onDrill }: LiveOpsTilesProps) {
  const clock = data.sla.clock;
  return (
    <TileGrid columns={{ xs: 1, sm: 2, md: 3, lg: 5 }} sx={{ mb: 4 }}>
      <JumpTile
        label="Open RFQs to quote"
        value={nf(data.open_rfqs.count)}
        detail={
          <>
            {nf(emailCount)} by email · {nf(portalCount)} portal customers
            <br />
            {overdue} waiting over 4 h · {backlogCount} waiting for the morning
          </>
        }
        action="See the queue"
        onClick={() => jumpTo('live-queue')}
      />
      <StatTile
        tile={{
          id: 'today',
          label: 'RFQs in the system today',
          value: nf(data.ingest.rfqs_today),
          detail: `${data.ingest.rfq_emails_read_today != null ? `${nf(data.ingest.rfq_emails_read_today)} RFQ emails read today, incl. ones deleted since · ` : ''}${nf(data.ingest.rfqs_last_24h)} in the last 24 h · a usual weekday brings ${nf(data.ingest.median_rfqs_per_weekday_28d)}`,
          drill: drills.receivedToday,
        }}
        onDrill={onDrill}
      />
      <StatTile
        tile={{
          id: 'sent',
          label: 'Quotes sent from the system today',
          value: nf(data.today.system_sent),
          detail: `Plus ${nf(data.today.manual_replies)} replied by hand, as of the last mailbox scan (${whenSgt(data.sla.scanned_through)})`,
          drill: drills.systemQuotesToday,
        }}
        onDrill={onDrill}
      />
      <StatTile
        tile={{
          id: 'speed',
          label: 'Answered within 4 hours',
          value: formatPct(clock?.last_7d.pct_within_4h, 0),
          detail: clock
            ? `of ${nf(clock.last_7d.answered)} answered office-hours RFQs, last 7 days · median first reply ${formatClock(clock.last_7d.median_minutes)} · ${formatPct(clock.last_7d.pct_within_1h)} within 1 h`
            : 'No replies on file',
          drill: drills.answeredLast7d,
        }}
        onDrill={onDrill}
      />
      <JumpTile
        label="Needs a look"
        value={nf(failureTotal)}
        tone={failureTotal > 0 ? 'bad' : undefined}
        detail="Emails the system could not read and Excel fills that failed, last 7 days"
        action="See the list"
        onClick={() => jumpTo('live-failures')}
      />
    </TileGrid>
  );
}

interface DailySpeed {
  label: string;
  within_1h: number | null;
  within_4h: number | null;
  answered: number;
  median: number | null;
}

/** Today by hour, and the last two weeks' answer speed. */
export function TodaySection({ data, hourly, daily }: { data: OpsHealth; hourly: ReturnType<typeof hourlyToday>; daily: DailySpeed[] }) {
  return (
    <Section id="live-today" title="Today and the last two weeks">
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Today by hour"
            subtitle={`RFQs created and quotes sent from the system are live. Replies by hand come from the mailbox scan (last ${whenSgt(data.sla.scanned_through)}).`}
            height={220}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID} />
                <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: BASELINE }} minTickGap={12} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} allowDecimals={false} width={28} />
                <Tooltip {...TOOLTIP} cursor={{ fill: CURSOR_FILL }} />
                <Legend {...LEGEND} />
                <Bar dataKey="received" name="RFQs created" fill={NEUTRAL} radius={[4, 4, 0, 0]} maxBarSize={14} />
                <Bar dataKey="system" name="Quotes from the system" stackId="out" fill={SERIES.system} maxBarSize={14} {...GAP} />
                <Bar dataKey="manual" name="Replies by hand" stackId="out" fill={SERIES.manual} radius={[4, 4, 0, 0]} maxBarSize={14} {...GAP} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Answered within 1 hour and 4 hours, by day"
            subtitle="Office-hours RFQs by the day they arrived, last 14 days; clock time to the first email reply. The tooltip gives how many were answered."
            height={220}
            empty={daily.length === 0 ? 'No replies on file for the last 14 days.' : null}
            footer={
              <Typography variant="caption" color="text.secondary">
                {nf(data.sla.unmatched_mailbox_threads_14d)} RFQ threads in the mailbox (last 14 days) have no email reply. That is not a to-do list: most
                were answered in a customer portal (Wrist, Seven Seas, ARC) or by phone.
              </Typography>
            }
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={GRID} />
                <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: BASELINE }} minTickGap={12} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} width={44} domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} />
                <Tooltip
                  {...TOOLTIP}
                  formatter={(value, name, item) => [
                    value == null ? '-' : `${value}% of ${item.payload.answered} answered (median ${formatClock(item.payload.median)})`,
                    name,
                  ]}
                />
                <Legend {...LEGEND} />
                <Line type="linear" dataKey="within_4h" name="Within 4 h" stroke={SERIES.violet} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} connectNulls={false} />
                <Line type="linear" dataKey="within_1h" name="Within 1 h" stroke={SERIES.aqua} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Section>
  );
}

/** Emails that could not be read and Excel fills that failed, last 7 days. */
export function FailuresSection({ data }: { data: OpsHealth }) {
  const [allFailures, setAllFailures] = useState(false);
  const failures = failureSummary(data);
  const failureItems = data.failures.items;
  return (
    <Section
      id="live-failures"
      title="Needs a look"
      subtitle="Last 7 days. Emails that read like an RFQ but gave no items may be follow-ups - check whether each was quoted. One entry per email (subject and sender); an email left unread fails again on every check."
    >
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {failures.byKind.map((k) => (
          <Chip key={k.kind} variant="outlined" label={`${k.label}: ${nf(k.n)}`} color={k.n > 0 && (k.kind === 'rfq_parse_failed' || k.kind === 'excel_job_stuck') ? 'error' : 'default'} />
        ))}
      </Stack>
      <Panel sx={{ height: 'auto' }}>
        {failureItems.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 1 }}>
            Nothing failed in the last 7 days.
          </Typography>
        ) : (
          <Stack spacing={1.5} component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {(allFailures ? failureItems : failureItems.slice(0, 8)).map((f, i) => (
              <Box
                component="li"
                key={`${f.kind}-${f.at}-${i}`}
                sx={{ borderLeft: 3, borderColor: f.kind === 'rfq_parse_failed' || f.kind === 'excel_job_stuck' ? 'error.main' : 'warning.main', pl: 1.5 }}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {FAILURE_LABELS[f.kind]}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {whenSgt(f.at)}
                    {f.attempts > 1 ? ` · failed ${f.attempts} times` : ''}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {f.subject || (f.rfq_id ? `RFQ ${f.rfq_id}` : '-')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {[f.sender, f.detail].filter(Boolean).join(' · ')}
                </Typography>
                {f.rfq_id && (
                  <Box>
                    <Button size="small" component={RouterLink} to={`/?tab=${rfqListTab(f.rfq_status)}&rfq=${f.rfq_id}`} sx={{ px: 0 }}>
                      Open RFQ {f.rfq_id}
                    </Button>
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        )}
        {failureItems.length > 8 && (
          <Button size="small" onClick={() => setAllFailures((v) => !v)} endIcon={allFailures ? <ExpandLess /> : <ExpandMore />} sx={{ mt: 1 }} aria-expanded={allFailures}>
            {allFailures ? 'Show fewer' : `Show all ${failureItems.length} listed`}
          </Button>
        )}
        {failures.listed < failures.total && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {nf(failures.total)} in all; the list holds the newest 25 of each kind ({nf(failures.listed)}). The History tab lists every one by day.
          </Typography>
        )}
      </Panel>
    </Section>
  );
}
